import type { ReactNode } from 'react'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../../shopifyUiTracking'

/**
 * Shared "why this matters / where the numbers come from" popover, and a page-level Help
 * modal, extracted from insights/InsightsBlocks.tsx's InsightSection so App Home and Tests Index
 * can use the same mechanism without the Insights-specific per-section
 * load/retry machinery that InsightSection also carries.
 */

/** The info icon button that opens an InfoPopover, placed in a section's `accessory` slot. */
export function InfoAccessoryButton({ id, heading }: { id: string; heading: string }) {
  const shopify = useAppBridge()
  return (
    <s-button
      slot="accessory"
      variant="tertiary"
      icon="info"
      commandFor={id}
      onClick={() => trackShopifyAction(shopify, 'open-info', () => undefined)}
      accessibilityLabel={`Why this matters: ${heading}`}
    />
  )
}

/**
 * The same info icon button with no slot, for sections that build their own heading row by hand
 * instead of passing a `heading` prop to `<s-section>` (the `accessory` slot only renders
 * alongside a section's own heading).
 */
export function InfoButton({ id, heading }: { id: string; heading: string }) {
  const shopify = useAppBridge()
  return (
    <s-button
      variant="tertiary"
      icon="info"
      commandFor={id}
      accessibilityLabel={`Why this matters: ${heading}`}
      onClick={() => trackShopifyAction(shopify, 'open-info', () => undefined)}
    />
  )
}

export function InfoPopover({ id, why, how }: { id: string; why: string; how: string }) {
  return (
    <s-popover id={id} maxInlineSize="340px">
      <s-box padding="base">
        <s-stack gap="small">
          <s-text fontWeight="semibold">Why this matters</s-text>
          <s-paragraph>{why}</s-paragraph>
          <s-text fontWeight="semibold">Where the numbers come from</s-text>
          <s-paragraph color="subdued">{how}</s-paragraph>
        </s-stack>
      </s-box>
    </s-popover>
  )
}

/** A page-level support/help affordance: a secondary-action button that opens an in-app modal. */
export function HelpButton({ id, label = 'Help' }: { id: string; label?: string }) {
  const shopify = useAppBridge()
  return (
    <s-button
      slot="secondary-actions"
      icon="question-circle"
      commandFor={id}
      onClick={() => trackShopifyAction(shopify, 'open-help', () => undefined)}
    >
      {label}
    </s-button>
  )
}

/**
 * The same page-level help affordance as `HelpButton`, but placed as a centered, subdued text
 * link at the bottom of the page body — Shopify's documented "Footer help" composition for App
 * Home, rather than a header button.
 */
export function FooterHelp({
  modalId,
  lead = 'Learn more about',
  link,
}: {
  modalId: string
  lead?: string
  link: string
}) {
  const shopify = useAppBridge()
  return (
    <s-stack alignItems="center" paddingBlock="base">
      <s-text color="subdued">
        {lead}{' '}
        <s-link commandFor={modalId} onClick={() => trackShopifyAction(shopify, 'open-help', () => undefined)}>
          {link}
        </s-link>
        .
      </s-text>
    </s-stack>
  )
}

export function HelpModal({ id, heading, children }: { id: string; heading: string; children: ReactNode }) {
  const shopify = useAppBridge()
  return (
    <s-modal id={id} heading={heading}>
      <s-stack gap="base">{children}</s-stack>
      <s-button
        slot="secondary-actions"
        commandFor={id}
        command="--hide"
        onClick={() => trackShopifyAction(shopify, 'close-help', () => undefined)}
      >
        Close
      </s-button>
    </s-modal>
  )
}
